import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { PrinterOutlined, FileTextOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Table, Card, Row, Col, Statistic, Descriptions, Tag } from "antd";
import { get_registry_details } from "../../../../Controllers/Registry";
import Header from "../../../../Componets/Header";
import Loadercomponent from "../../../../Componets/Loader";
import RegistryPrint from "./RegistryPrint";
import RegistrySummaryPrint from "./RegistrySummaryPrint";
import headers from "../../../../Data/Forms/Billings/Session/headers";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";


const RegistryView = (props) => {
    let { id } = useParams();
    let navigate = useNavigate();
    const [header, setHeader] = useState(headers);


    const registryPrintRef = useRef();
    const registrySummaryPrintRef = useRef();

    const handleRegistryPrint = useReactToPrint({
        content: () => registryPrintRef.current,
        documentTitle: `Registry_${id}_Details`
    });

    const handleSummaryPrint = useReactToPrint({
        content: () => registrySummaryPrintRef.current,
        documentTitle: `Registry_${id}_Summary`
    });

    const [loader, setLoader] = useState(false);
    const [registry, setRegistry] = useState(null);

    const handleData = (registryData) => {
        setRegistry(registryData);
        setLoader(false);
    };

    const fetchData = async () => {
        if (id) {
            setLoader(true);
            const data = { id: id };

            const registryData = await get_registry_details(data);
            if (registryData?.status) {
                handleData(registryData?.data);
            } else {
                toast.error(registryData?.message || "Failed to fetch registry details");
            }
        } else {
            setLoader(false);
            toast.error("Registry ID is required");
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const invoiceColumns = [
        {
            title: 'Invoice #',
            dataIndex: 'number',
            key: 'number',
            width: 100,
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => new Date(date).toLocaleDateString(),
            width: 120,
        },
        {
            title: 'Customer',
            dataIndex: 'customer',
            key: 'customer',
            render: (customer) => customer ? customer.name || 'Walk-in Customer' : 'Walk-in Customer',
            width: 150,
        },
        {
            title: 'Subtotal',
            dataIndex: 'subtotal',
            key: 'subtotal',
            render: (amount) => `${amount?.toFixed(2) || '0.00'}`,
            width: 100,
            align: 'right',
        },
        {
            title: 'Tax',
            dataIndex: 'taxamount',
            key: 'taxamount',
            render: (amount) => `${amount?.toFixed(2) || '0.00'}`,
            width: 100,
            align: 'right',
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: (amount) => `${amount?.toFixed(2) || '0.00'}`,
            width: 100,
            align: 'right',
        },
        {
            title: 'Payment Status',
            dataIndex: 'payment_status',
            key: 'payment_status',
            render: (status) => {
                const statusMap = {
                    0: { text: 'Pending', color: 'orange' },
                    1: { text: 'Overdue', color: 'red' },
                    2: { text: 'Paid', color: 'green' }
                };
                const statusInfo = statusMap[status] || { text: 'Unknown', color: 'default' };
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
            width: 120,
        },
        {
            title: 'Delivery Status',
            dataIndex: 'delivery_status',
            key: 'delivery_status',
            render: (status) => {
                const statusMap = {
                    0: { text: 'Pending', color: 'orange' },
                    1: { text: 'Delivered', color: 'green' },
                    2: { text: 'Processing', color: 'blue' },
                };
                const statusInfo = statusMap[status] || { text: 'Unknown', color: 'default' };
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
            width: 120,
        }
    ];

    // Date formatting utility functions
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';

        const date = new Date(dateString);
        let hours = date.getHours();
        let minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${hours}:${minutes} ${ampm}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return `${formatDate(dateString)} ${formatTime(dateString)}`;
    };


    const cashColumns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'in' ? 'green' : 'red'}>
                    {type === 'in' ? 'CASH IN' : 'CASH OUT'}
                </Tag>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `$${amount?.toFixed(2)}`,
            align: 'right',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Date & Time',
            dataIndex: 'createdAt',
            key: 'date',
            render: () => (
                <div>
                    <div>{formatDateTime(registry.updatedAt)}</div>
                </div>
            ),
        },
    ];

    if (loader) {
        return <Loadercomponent loader={loader} />;
    }

    if (!registry) {
        return (
            <div className="registry-view-container">
                <div className="text-center p-8">
                    <p>No registry data found</p>
                </div>
            </div>
        );
    }

    return (
        <Header headers={header} setHeaders={setHeader} >
            <ScrollWrapper>
                <Loadercomponent loader={loader}>
                    <div style={{ marginTop: "50px", margin: "1%" }}>
                        <div className="pb-8">
                            <div className="flex justify-between items-center">
                                <div className="flex">
                                </div>
                                <div className="flex">
                                    <div className="p-2" onClick={handleRegistryPrint}>
                                        <Button size="large" type="primary">
                                            <PrinterOutlined /> Print Detailed Report
                                        </Button>
                                    </div>
                                    <div className="p-2" onClick={handleSummaryPrint}>
                                        <Button size="large">
                                            <FileTextOutlined /> Print Summary
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Row gutter={[16, 16]} className="mb-6">
                            <Col xs={24} sm={12} md={6}>
                                <Card>
                                    <Statistic
                                        title="Opening Balance"
                                        value={registry.openingBalance}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card>
                                    <Statistic
                                        title="Closing Balance"
                                        value={registry.closingBalance}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card>
                                    <Statistic
                                        title="Total Sales"
                                        value={registry.totalSales}
                                        precision={2}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Card>
                                    <Statistic
                                        title="Total Invoices"
                                        value={registry.totalInvoices}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={8}>
                                <Card title="Registry Information" className="mb-4">
                                    <Descriptions column={1} bordered size="small">
                                        <Descriptions.Item label="Status">
                                            <Tag color={registry.isOpen ? 'green' : 'red'}>
                                                {registry.isOpen ? 'OPEN' : 'CLOSED'}
                                            </Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Created By">
                                            {registry.createdBy?.name}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Date">
                                            {new Date(registry.date).toLocaleString()}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Cash Difference">
                                            <Tag color={registry.cashDifference >= 0 ? 'green' : 'red'}>
                                                {registry.cashDifference?.toFixed(2)}
                                            </Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Counted Cash">
                                            {registry.countedCash?.toFixed(2)}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                {(registry.cashIn?.length > 0 || registry.cashOut?.length > 0) && (
                                    <Card title="Cash Movements" className="mb-4">
                                        <Table
                                            dataSource={[
                                                ...(registry.cashIn?.map(item => ({ ...item, type: 'in' })) || []),
                                                ...(registry.cashOut?.map(item => ({ ...item, type: 'out' })) || [])
                                            ]}
                                            columns={cashColumns}
                                            pagination={false}
                                            size="small"
                                            scroll={{ x: 400 }}
                                        />
                                    </Card>
                                )}

                                {registry.notes && (
                                    <Card title="Notes">
                                        <p>{registry.notes}</p>
                                    </Card>
                                )}
                            </Col>

                            <Col xs={24} lg={16}>
                                <Card title={`Invoices (${registry.invoiceIds?.length || 0})`}>
                                    <Table
                                        dataSource={registry.invoiceIds || []}
                                        columns={invoiceColumns}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showQuickJumper: true,
                                            showTotal: (total, range) =>
                                                `${range[0]}-${range[1]} of ${total} items`
                                        }}
                                        scroll={{ x: 800 }}
                                        rowKey="_id"
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>
                    <div className="hidden-on-screen">
                        <RegistryPrint ref={registryPrintRef} registry={registry} />
                    </div>
                    <div className="hidden-on-screen">
                        <RegistrySummaryPrint ref={registrySummaryPrintRef} registry={registry} />
                    </div>
                </Loadercomponent>
            </ScrollWrapper>
        </Header>
    );
};

export default RegistryView;